<script lang="ts" setup>
import GwDeleteDialog, { type DeleteType } from '@/components/lib/GwDeleteDialog/GwDeleteDialog.vue'
import GwDragDropper from '@/components/lib/GwDragDropper/GwDragDropper.vue'
import { type GwScrollContProps } from '@/components/lib/GwScrollCont/GwScrollCont.vue'
import GwTable from '@/components/lib/GwTable/GwTable.vue'
import GwTableRow from '@/components/lib/GwTable/GwTableRow.vue'
import GwUiType from '@/components/lib/GwTable/GwUiType.vue'
import { sendGaEvent } from '@gto/analytics/google-analytics'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import { type DragDropArgs } from '@/components/lib/GwDragDropper/GwDragDropperUtils'
import { CALL_DEBOUNCE_MS } from '@/helpers/constantsNew'
import AnalyzeHandIcons from '@/components/Analyze/AnalyzeHand/AnalyzeHandIcons.vue'
import { Portal } from 'portal-vue'
import { computed, ref, watch, onUnmounted } from 'vue'

import {
  extractColumnStyles,
  getOrderingParams,
  type GwTableCheckBoxState,
  type TReportTableColumn,
} from '@/components/lib/GwTable/gwTableUtils'
import { t, tc } from '@/locales/i18n'
import {
  getHands,
  handsQueryKey,
  type FilterParams,
  type GetHandsResponse,
  type SummaryColumnsT,
} from '@/store/api/gql/hands/queries'
import type {
  ColumnFieldName,
  Filter,
  HandHistoryReportColumn,
  ReportTableRow,
} from '@/types/api/reports'
import type { GwError } from '@/types/api/basic'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useStore } from 'vuex'
import * as R from 'remeda'
import GwTransition from '@/components/lib/GwTransition/GwTransition.vue'
import type { ColumnManagementEmits } from '@/components/hhv3/Analyzer/ReportTable/ColumnManagement.vue'
import GwTableMessages from '@/components/lib/GwTable/GwTableMessages.vue'
import { useRouter } from 'vue-router'
import { all_streets } from '@/helpers/constants'
import { deleteHandsMutation } from '../../../../store/api/gql/hands/mutations'
import { generateFilterParams } from '../../../../store/api/gql/hands/utils'
import type { ID } from '../../../../types/basic'
import type {
  GwPaginationEmitProps,
  GwPaginationProps,
} from '../../../lib/GwPagination/gwPaginationUtils'
import { numKtoM } from '../../../../helpers/formating'
import SummaryItem from './SummaryItem.vue'

export interface EmitSelectType {
  headCheckBoxState: GwTableCheckBoxState
  selectedItems: ID[]
}

export interface Props {
  columns: HandHistoryReportColumn[]
  filters: Filter[]
  reportId: ID
}

const props = defineProps<Props>()
const store = useStore()
const router = useRouter()

const limitOffset = ref<GwPaginationEmitProps>({
  limit: 100,
  offset: 0,
})

const selectedItems = ref<ReportTableRow['hand_id'][]>([])
const checkboxState = ref<GwTableCheckBoxState | undefined>(undefined)

const sortConf = computed(() => {
  const activeColumn = props.columns.find(col => col.sort_direction)
  return {
    sortKey: activeColumn?.column.field_name,
    sortOrder: activeColumn?.sort_direction || 'ASC',
  }
})

const orderingParams = computed(() => getOrderingParams<ColumnFieldName>(sortConf.value))

const tableColumns = computed(
  () =>
    props.columns
      .filter(({ is_hidden }) => !is_hidden)
      .map(tableColumn => ({
        field: tableColumn.column.field_name,
        id: tableColumn.column.id,
        column: tableColumn.column,
        name: t(`hhv3.table_head.${tableColumn.column.field_name}`),
        sortKey: tableColumn.column.is_ordering_available && tableColumn.column.field_name,
        uiType: tableColumn.column.ui_value_type,
        sort_direction: tableColumn.sort_direction,
        severities: tableColumn.column.ui_colors,
        ...extractColumnStyles(tableColumn.column.ui_value_type, tableColumn.column.field_name),
      })) as TReportTableColumn[],
)

const fieldsToFetch = computed(() => tableColumns.value.map(({ field }) => field))
const activeFilters = computed(() => generateFilterParams(props.filters))

const summaryColumns = computed(() =>
  props.columns
    .filter(({ is_hidden, column: { totals_field_name } }) => !is_hidden && totals_field_name)
    .reduce<SummaryColumnsT>(
      (acc, { column: { totals_field_name, field_name } }) => ({
        ...acc,
        [field_name]: totals_field_name,
      }),
      {},
    ),
)

const queryKey = computed(() => [
  ...handsQueryKey,
  // sort() disables fetching of data when the order of the fields changes
  fieldsToFetch.value.slice().sort(),
  limitOffset.value,
  sortConf.value,
  activeFilters.value,
])

const debouncedQueryKey = ref(queryKey.value)
const queryDebouncer = R.debounce(
  value => {
    debouncedQueryKey.value = value
  },
  { waitMs: CALL_DEBOUNCE_MS },
)

watch(queryKey, (currentQueryKey, oldQueryKey) => {
  if (!R.isDeepEqual(currentQueryKey, oldQueryKey)) {
    queryDebouncer.call(queryKey.value)
  }
})

watch([sortConf, activeFilters], () => {
  limitOffset.value = { ...limitOffset.value, offset: 0 }
})

type deleteHandsMutationAttributes = {
  currentActiveFilters: FilterParams
  currentQueryKey: typeof queryKey.value
  currentSelectedItems: ID[]
  deleteType: DeleteType
}

const queryClient = useQueryClient()
const { mutate } = useMutation({
  mutationFn: ({
    currentActiveFilters,
    currentQueryKey: _currentQueryKey,
    currentSelectedItems,
    deleteType,
  }: deleteHandsMutationAttributes) =>
    deleteHandsMutation({
      deleteType,
      selectedItems: currentSelectedItems,
      activeFilters: currentActiveFilters,
    }),
  onMutate: async ({ deleteType, currentQueryKey, currentSelectedItems }) => {
    await queryClient.cancelQueries({ queryKey: handsQueryKey })

    const previousHands = queryClient.getQueryData(currentQueryKey) as object

    const createNewHands = ({ hands, hand_statistics }: GetHandsResponse) =>
      deleteType === 'all'
        ? { hand_statistics: { count_hands: 0 }, hands: [] }
        : {
            hand_statistics: {
              count_hands: (hand_statistics.count_hands ?? 0) - currentSelectedItems.length,
            },
            hands: hands.filter(hand => !currentSelectedItems.includes(hand.hand_id)),
          }

    queryClient.setQueryData(currentQueryKey, createNewHands)

    return { previousHands, currentQueryKey }
  },
  onSuccess: async () => queryClient.invalidateQueries({ queryKey: handsQueryKey }),
  onError: (err, _deleteType, context) => {
    store.dispatch('messages', {
      type: 'error',
      text: `${t('messages.oops_error')} <br/> <br/> ${err.message}`,
    })
    queryClient.setQueryData(context!.currentQueryKey, context?.previousHands)
  },
})

const { data, isFetching, error } = useQuery<GetHandsResponse, GwError>({
  queryKey: debouncedQueryKey,
  queryFn: getHands({
    fields: fieldsToFetch,
    pagination: limitOffset,
    ordering: orderingParams,
    filters: activeFilters,
    summaryColumns,
  }),
  refetchOnWindowFocus: false,
  placeholderData: R.identity(),
})
const shouldBeLoading = computed(() => !R.isDeepEqual(queryKey.value, debouncedQueryKey.value))
const loading = computed(() => shouldBeLoading.value || isFetching.value)

const getSummaryValue = computed(() => (column: ColumnFieldName) => {
  const fieldName = summaryColumns.value[column]
  return fieldName && data.value?.hand_statistics[fieldName]
})

const gwPaginationProps = computed<GwPaginationProps>(() => ({
  loading: isFetching.value,
  count: data.value?.hand_statistics.count_hands,
  ...limitOffset.value,
}))

const gwScrollContProps: GwScrollContProps = {
  direction: 'omni',
  scrollbar: 'large',
  hasDragScroll: false,
  hasEdgeAutoScroll: false,
  hasOverflowSurfaces: false,
  overflowSurfaceSize: 'large',
}

const visibleColumns = computed(
  () =>
    tableColumns.value.map(column => {
      if (column.field === 'actions_with_correctness') {
        return {
          ...column,
          subColumns: all_streets.map((street, i) => ({
            ...column,
            subColumn: street,
            name: t(`hhv3.table_head.${street}`),
            width: `${Math.max(
              ...(data.value?.hands?.map(
                hand => hand?.actions_with_correctness?.[i]?.length ?? 0,
              ) ?? []),
            )}rem`,
          })),
        }
      }
      return column
    }) as TReportTableColumn[],
)
const getDraggedColumn = (item: TReportTableColumn) => {
  const draggedColumnIndex = visibleColumns.value.findIndex(column => column.field === item.field)
  visibleColumns.value[draggedColumnIndex].isDragging = true
  return props.columns.find(({ column }) => column.field_name === item.field)
}

const endDrag = (item: TReportTableColumn) => {
  const draggedColumnIndex = visibleColumns.value.findIndex(column => column.field === item.field)
  visibleColumns.value[draggedColumnIndex].isDragging = false
}

const getDraggedTableColumnName = (item: TReportTableColumn) =>
  visibleColumns.value.find(({ id }) => id === item.id)?.name || 'error'

const handlePaginationUpdate = (args: GwPaginationEmitProps) => {
  limitOffset.value = {
    limit: args.limit,
    offset: args.offset,
  }
}

const handleCheckboxStateUpdate = ({
  headCheckBoxState,
  selectedItems: newSelectedItems,
}: EmitSelectType) => {
  checkboxState.value = headCheckBoxState
  selectedItems.value = newSelectedItems
}

const handleDeleteHands = (deleteType: DeleteType) => {
  mutate({
    deleteType,
    currentSelectedItems: selectedItems.value,
    currentActiveFilters: activeFilters.value,
    currentQueryKey: queryKey.value,
  })
}

export type ReportTableEmits = {
  'drag:drop:column': ColumnManagementEmits['drag:drop:column']
  'th:click': [Event, TReportTableColumn]
}
const emit = defineEmits<ReportTableEmits>()
const handleDrop = (e: Event, args: DragDropArgs<HandHistoryReportColumn>) => {
  emit('drag:drop:column', e, args)
}

const errorCode = computed(() => error.value?.response?.status)
const globals_drawer = computed(() => store.getters.globals_drawer)

const handID = ref<ID>()
const openHandDetail = (id: ID) => {
  handID.value = id
  store.dispatch('globals_drawer', {
    id: 'analyze_handV4',
    open: true,
    content: {
      hand_id: id,
    },
    maximized: store.getters.globals_drawer.maximized,
  })
  router.replace({
    query: {
      hand_detail: id,
    },
  })
}
const hotkeysPressedBottom = computed(() => store.getters.hotkeys_pressed_bottom)
const go = (direction: number) => {
  if (!data.value?.hands.length) return

  const index = data.value.hands.findIndex(hand => hand.hand_id === handID.value)
  if (index === -1) return

  const newIndex = (index + direction + data.value.hands.length) % data.value.hands.length

  openHandDetail(data.value.hands[newIndex].hand_id)
}

watch(
  () => hotkeysPressedBottom.value,
  (_oldVal, keys) => {
    if (!keys) return
    const hotkeyActions = {
      go_fw: { keys: ['arrowdown'], fn: () => go(1), hasPreventDefault: true },
      go_rw: { keys: ['arrowup'], fn: () => go(-1), hasPreventDefault: true },
    }
    store.dispatch('hotkeys_perform_action', { keys, hotkeyActions })
  },
)

watch(
  () => props.reportId,
  () => {
    debouncedQueryKey.value = queryKey.value
  },
)

const isUnmounted = ref(false)

onUnmounted(() => {
  isUnmounted.value = true
})

// Watch for the data to be loaded before sending the alz_view_opened event
watch(
  () => Boolean(data.value?.hands),
  () => {
    // Added this timeout to ensure that the data is loaded before sending the event
    // Also this prevents false positives when stats should be loaded instead but there is no redirect yet.
    setTimeout(() => {
      if (isUnmounted.value) return

      sendGaEvent({
        event: 'page_opened',
        page: 'analyzer',
        view: 'hands',
      })
    }, 3000)
  },
)
</script>
<template>
  <Portal to="nav_icons">
    <AnalyzeHandIcons
      class="mr-auto gw_box"
      style="padding: 0; --clr-box-back: var(--clr-black-1-3)"
      :hasStarBtn="false"
      :hasHotkeys="false"
      :isAlwaysEnabled="true"
      @click:go="go"
    />
  </Portal>

  <GwDragDropper
    dropTargetIdPart="th_"
    direction="horizontal"
    @drag:drop="handleDrop"
  >
    <template #shallowitem="{ draggedData }">
      <GwBox
        v-if="draggedData"
        variant="box_dragDrop_item"
      >
        <span v-html="getDraggedTableColumnName(draggedData.column as TReportTableColumn)" />
      </GwBox>
    </template>
    <template #default="{ methods, isDragging }">
      <GwTable
        tstPrefix="report"
        hasCheckboxes
        hasNotes
        hasColors
        variant="table_default"
        :loading
        :error
        :items="data?.hands || []"
        :gwPaginationProps
        :gwScrollContProps="{ ...gwScrollContProps, hasEdgeAutoScroll: isDragging }"
        :sortConf="sortConf"
        :columns="visibleColumns"
        idKey="hand_id"
        style="--th-pad-left: 1rem"
        @th:click="
          (e, item) => {
            if (isDragging) return
            emit('th:click', e, item)
          }
        "
        @update:headCheckboxState="handleCheckboxStateUpdate"
        @update:pagination="handlePaginationUpdate"
        @th:mousedown="(event, column) => methods.startDelayedDrag(event, getDraggedColumn(column))"
        @th:touchstart="
          (event, column) => methods.startDelayedDrag(event, getDraggedColumn(column))
        "
        @th:mouseup="
          (_, column) => {
            endDrag(column)
            methods.endDelayedStartDrag()
          }
        "
        @th:touchend="
          (_, column) => {
            endDrag(column)
            methods.endDelayedStartDrag()
          }
        "
      >
        <template #before>
          <GwTransition
            variant="slidebottom_fade"
            appear
          >
            <div
              v-if="selectedItems.length && !loading"
              class="f-center w-100"
              style="
                position: fixed;
                bottom: calc(
                  var(--app-safe-area-inset-bottom) + var(--app-footer-height) + var(--app-pad-5)
                );
                left: 0;
                z-index: 8;
              "
            >
              <GwBox variant="box_menu">
                <GwDeleteDialog
                  data-tst="open_delete_hands_dialog"
                  :btnOpenerText="t('hhv3.delete_hands_dialog.title')"
                  :modalTitle="t('hhv3.delete_hands_dialog.title')"
                  :modalText="tc('hhv3.delete_hands_dialog.text', selectedItems.length)"
                  :btnDeleteText="tc('hhv3.delete_hands_dialog.delete_text', selectedItems.length)"
                  :btnDeleteAllText="
                    checkboxState === 'all' &&
                    selectedItems.length !== data?.hand_statistics.count_hands
                      ? tc('hhv3.delete_hands_dialog.delete_all_text', {
                          num: numKtoM(data?.hand_statistics.count_hands || 0, 1000, 2),
                        })
                      : ''
                  "
                  :modalTextDeleteAllPrompt="
                    tc('hhv3.delete_hands_dialog.delete_all_text_prompt', {
                      num: numKtoM(data?.hand_statistics.count_hands || 0, 1000, 2),
                    })
                  "
                  @delete="handleDeleteHands"
                />
              </GwBox>
            </div>
          </GwTransition>
        </template>

        <template #error>
          <GwTableMessages
            class="anim-fade-in"
            message="error"
            :hasRefreshBtn="true"
            :errorCode
          />
        </template>
        <template #no_data>
          <GwTableMessages
            v-if="activeFilters.length"
            class="anim-fade-in"
            message="filter_no_hands"
          />
          <GwTableMessages
            v-else
            class="anim-fade-in"
            message="upload_hands"
            @click:upload="store.dispatch('globals_drawer_toggle')"
          />
        </template>

        <template
          #row="{ item, index, methods: rowMethods, selectedItems: rowSelectedItems, variantConf }"
        >
          <GwTableRow
            :id="item.hand_id"
            hasCheckbox
            hasNote
            hasColor
            :note="item.note"
            :colorHex="item.color_hex"
            :selectedItems="rowSelectedItems"
            :data-tst="`tr_${index}`"
            :variantConf
            :active="item.hand_id === globals_drawer?.content?.hand_id"
            @click:checkbox="rowMethods.selectItem"
          >
            <td
              v-for="col in visibleColumns"
              :key="`${col.id}_${item.hand_id}`"
              :data-tst="`td_${col.field}`"
              :class="col.isDragging && 'clr-back-black-2'"
              @click="e => openHandDetail(item.hand_id)"
            >
              <div class="d-flex">
                <GwUiType
                  v-for="(column, i) in col.subColumns || [col]"
                  :key="`${column.id}_${item.hand_id}`"
                  class="flex-1"
                  :style="{ 'min-width': column.width || 'auto' }"
                  :value="
                    col.subColumns && item[column.field]
                      ? item[column.field]![i]
                      : item[column.field]
                  "
                  :uiType="column.uiType"
                  :severities="column.severities"
                  isTableValue
                />
              </div>
            </td>
          </GwTableRow>
        </template>
        <template #default_after="{ variantConf }">
          <tbody
            v-if="data?.hands.length"
            class="h-100"
          >
            <tr class="h-100"></tr>
            <GwTableRow
              class="no-events"
              style="position: sticky; bottom: 0; z-index: 7; background: var(--app-surface-table)"
              :variantConf
            >
              <td />
              <td
                v-for="column in visibleColumns"
                :id="column.field"
                :key="column.id"
                class="text-nowrap"
              >
                <SummaryItem
                  v-if="R.isNumber(getSummaryValue(column.field))"
                  :column
                  :value="getSummaryValue(column.field)!"
                />
              </td>
            </GwTableRow>
          </tbody>
        </template>
      </GwTable>
    </template>
  </GwDragDropper>
</template>
